import React, { useState, useEffect } from 'react';
import { Box, Card, Typography, Breadcrumbs, Divider, Button } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { Formik, Form } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { useResponseHandler } from 'hooks/useResponseHandler';
import formInitialValues from './formik/formInitialValues';
import validationSchema from './formik/validationSchema';
import assistantsFormModel from './formik/assistantsFormModel';
import { AsistentesForm } from './components/forms/AsistentesForm';
import { saveAssistantStore, getDataFormDropDownStore } from 'store/assistants/thunks';
import { MessagesDrawerRight } from 'components/MessagesDrawerRight';
import { Alert, LinearProgress, Chip, Stack } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { assistantsMapper } from './mappers/assistants.mapper';

export const CreateAsistentes = () => {
    const dispatch:any = useDispatch();
    const navigate = useNavigate();
    const { handleResponse, ResponseComponents } = useResponseHandler();
    const { formId, formField } = assistantsFormModel;
    const [data, setMsgErrors] = useState([]);

    const { form_options, form_options_ia_skills_checkbox, plan_info } = useSelector((state: any) => state.assistants);

    useEffect(() => {
        getDataForm();
    }, []); 

    const getDataForm = async()=>{
        await dispatch(getDataFormDropDownStore());
    }

    const servicesOptions = form_options;

    const handleSubmit = async (values: any, actions: any) => {
        actions.setSubmitting(true);
        const dto = assistantsMapper.toDto(values);
        const response = await dispatch(saveAssistantStore(dto));
        
        await handleResponse(response, 'Asistente Creado Con Éxito', true);
        actions.setSubmitting(false);

        if(response.ok){
            actions.resetForm();
        }
        
    };

    return (
        <Box sx={{ width: '100%' }}>
            <MessagesDrawerRight data={data} title={''} />
            {ResponseComponents}

            <Typography fontSize={25} color="textPrimary" sx={{ fontWeight: 700 }}>Crear Asistente</Typography>
            <Breadcrumbs aria-label="breadcrumb" sx={{ padding: 2 }}>
                <Typography color="textPrimary" sx={{ fontWeight: 700 }}>Inteligencia Artificial</Typography>
                <Link color="inherit" to="/manager/plataforma-ia/asistentes">
                    Listar Asistentes
                </Link>
                <Typography color="textPrimary">Crear Asistente</Typography>
            </Breadcrumbs>
            
            <Divider sx={{ marginBottom: 3 }}/>

            {plan_info && plan_info.planName && (
                <Box sx={{ 
                    mb: 4, 
                    p: 3, 
                    borderRadius: 3, 
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    background: plan_info.canCreateAssistant 
                        ? 'linear-gradient(135deg, rgba(107, 76, 255, 0.05) 0%, rgba(168, 85, 247, 0.05) 100%)' 
                        : 'linear-gradient(135deg, rgba(239, 68, 68, 0.05) 0%, rgba(185, 28, 28, 0.05) 100%)',
                    border: '1px solid',
                    borderColor: plan_info.canCreateAssistant ? 'rgba(107, 76, 255, 0.2)' : 'rgba(239, 68, 68, 0.3)',
                    boxShadow: plan_info.canCreateAssistant 
                        ? '0 8px 32px rgba(107, 76, 255, 0.08)' 
                        : '0 8px 32px rgba(239, 68, 68, 0.1)'
                }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            {plan_info.canCreateAssistant ? (
                                <AutoAwesomeIcon sx={{ color: '#6b4cff' }} />
                            ) : (
                                <WarningAmberIcon sx={{ color: '#ef4444' }} />
                            )}
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: plan_info.canCreateAssistant ? '#4a36b3' : '#b91c1c' }}>
                                Resumen de Suscripción
                            </Typography>
                        </Box>
                        <Chip 
                            label={`Plan: ${plan_info.planName.toUpperCase()}`} 
                            size="small" 
                            sx={{ 
                                fontWeight: 700, 
                                bgcolor: plan_info.canCreateAssistant ? '#e0d8ff' : '#fee2e2',
                                color: plan_info.canCreateAssistant ? '#583be8' : '#b91c1c'
                            }} 
                        />
                    </Box>
                    
                    <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                                Asistentes creados
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 800, color: plan_info.canCreateAssistant ? '#6b4cff' : '#ef4444' }}>
                                {plan_info.currentAssistantsCount} / {plan_info.maxAssistants}
                            </Typography>
                        </Box>
                        <LinearProgress 
                            variant="determinate" 
                            value={(plan_info.currentAssistantsCount / plan_info.maxAssistants) * 100} 
                            sx={{ 
                                height: 8, 
                                borderRadius: 4,
                                bgcolor: plan_info.canCreateAssistant ? 'rgba(107, 76, 255, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                                '& .MuiLinearProgress-bar': {
                                    bgcolor: plan_info.canCreateAssistant ? '#6b4cff' : '#ef4444',
                                    borderRadius: 4
                                }
                            }}
                        />
                        {!plan_info.canCreateAssistant && (
                            <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: '#ef4444', fontWeight: 600 }}>
                                Has alcanzado el límite de asistentes permitido en tu plan actual. Por favor, actualiza tu suscripción para crear más.
                            </Typography>
                        )}
                    </Box>
                </Box>
            )}

            <Formik
                initialValues={formInitialValues}
                validationSchema={validationSchema[0]}
                onSubmit={handleSubmit}
            >
                {({ isSubmitting }) => (
                    <Card sx={{ 
                        mt: 2, 
                        p: 3, 
                        borderRadius: 3, 
                        boxShadow: '0px 12px 24px -4px rgba(107, 76, 255, 0.15)',
                        background: (theme) => `linear-gradient(145deg, ${theme.palette.background.paper} 0%, ${theme.palette.primary.light}08 100%)`,
                        border: 'none'
                    }}>
                        <Form id={formId} autoComplete="off">
                            <AsistentesForm formField={formField} servicesOptions={servicesOptions} skillsOptions={form_options_ia_skills_checkbox} />

                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 4, gap: 2 }}>
                                <Button 
                                    component={Link}
                                    to="/manager/plataforma-ia/asistentes"
                                    color="inherit" 
                                    sx={{ borderRadius: 2, textTransform: 'none' }}
                                >
                                    Cancelar
                                </Button>
                                <Button 
                                    type="submit" 
                                    variant="contained" 
                                    disabled={isSubmitting || plan_info?.canCreateAssistant === false}
                                    sx={{ 
                                        borderRadius: 2, 
                                        textTransform: 'none',
                                        px: 3,
                                        fontWeight: 600,
                                        background: 'linear-gradient(45deg, #6b4cff 30%, #a855f7 90%)',
                                        boxShadow: '0 4px 14px 0 rgba(107, 76, 255, 0.39)',
                                        '&:hover': {
                                            background: 'linear-gradient(45deg, #583be8 30%, #933ce6 90%)',
                                        }
                                    }}
                                >
                                    Crear Asistente
                                </Button>
                            </Box>
                        </Form>
                    </Card>
                )}
            </Formik>
        </Box>
    );
};
